import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useNotification from "../../../hooks/useNotification";
import opname from "../../../services/opname";
import queryDefault from "../../../utils/queryDefault";

interface OpnameItem {
  id: number;
  branch_id: number;
  branch_name: string;
  title: string;
  description: string;
  type: string;
  keyword: string;
  photo: string;
  price: number;
}

type OpnameFormData = {
  branch_id: number | string;
  title: string;
  type: string;
  keyword?: string;
  description?: string;
  photo?: string;
  price?: number | string;
};

export default function OpnamesTab() {
  const notification = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const { control, handleSubmit, reset } = useForm<OpnameFormData>({
    defaultValues: {
      branch_id: "",
      title: "",
      type: "recipe_book",
      keyword: "",
      description: "",
      photo: "",
      price: "",
    },
  });

  const { data: opnamesData, isLoading, refetch } = useQuery(
    ["opname-manage-list", page, rowsPerPage],
    () =>
      opname.getOpnameList({
        page: page + 1,
        limit: rowsPerPage,
      }),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch opnames",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const createOpnameMutation = useMutation(
    (data: OpnameFormData) =>
      opname.createOpname({
        ...data,
        branch_id: Number(data.branch_id),
        price: Number(data.price || 0),
      }),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Opname created successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to create opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const updateOpnameMutation = useMutation(
    (data: { id: number; payload: OpnameFormData }) =>
      opname.updateOpname(data.id, {
        ...data.payload,
        branch_id: Number(data.payload.branch_id),
        price: Number(data.payload.price || 0),
      }),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Opname updated successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        setEditingId(null);
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to update opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const deleteOpnameMutation = useMutation(
    (id: number) => opname.deleteOpname(id),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Opname deleted successfully",
          type: "success",
          position: "top",
        });
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to delete opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const items: OpnameItem[] = opnamesData?.data || [];
  const total = opnamesData?.pagination?.total || 0;

  const handleOpenDialog = (item?: OpnameItem) => {
    if (item) {
      setEditingId(item.id);
      reset({
        branch_id: item.branch_id,
        title: item.title,
        type: item.type,
        keyword: item.keyword,
        description: item.description,
        photo: item.photo,
        price: item.price,
      });
    } else {
      setEditingId(null);
      reset({
        branch_id: "",
        title: "",
        type: "recipe_book",
        keyword: "",
        description: "",
        photo: "",
        price: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    setEditingId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSubmit = (data: OpnameFormData) => {
    if (editingId) {
      updateOpnameMutation.mutate({ id: editingId, payload: data });
    } else {
      createOpnameMutation.mutate(data);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => handleOpenDialog()}>
          + Create Opname
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Keyword</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Price
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No opnames found
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.branch_name}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{item.type}</TableCell>
                  <TableCell>{item.keyword || "-"}</TableCell>
                  <TableCell align="right">{item.price || "-"}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          if (window.confirm("Are you sure?")) {
                            deleteOpnameMutation.mutate(item.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? "Edit Opname" : "Create Opname"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Controller
              name="branch_id"
              control={control}
              rules={{ required: "Branch is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Branch ID"
                  type="number"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="title"
              control={control}
              rules={{ required: "Title is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Title"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              rules={{ required: "Type is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem value="recipe_book">Recipe Book</MenuItem>
                  <MenuItem value="photostrip">Photostrip</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="keyword"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Keyword" />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Description" multiline rows={3} />
              )}
            />

            <Controller
              name="photo"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Photo URL" />
              )}
            />

            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Price" type="number" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={createOpnameMutation.isLoading || updateOpnameMutation.isLoading}
          >
            {editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
